import { brandLogoAssets } from "@/lib/brand";
import { cn } from "@/lib/utils";

type BrandLogoVariant = keyof typeof brandLogoAssets;

/** Renders approved logo marks from `brandLogoAssets`. UI colors use shadcn semantic tokens; use `brandHex` only where CSS variables are unavailable (email, OG images, map markers). */
export function BrandLogo({
  className,
  imageClassName,
  priority,
  variant = "wordmark",
}: {
  className?: string;
  imageClassName?: string;
  priority?: boolean;
  variant?: BrandLogoVariant;
}) {
  const asset = brandLogoAssets[variant];
  const imageClass = cn("h-full w-full object-contain", imageClassName);
  const srcDark = "srcDark" in asset ? asset.srcDark : undefined;

  // The image width attribute becomes the min-content size. Without an
  // aspect-ratio box and min-w-0, `h-8 w-auto` still lays out at the asset
  // pixel width (420px for the wordmark) and object-contain centers the mark
  // inside that overflow — which reads as an off-center logo on mobile.
  return (
    <span
      className={cn("inline-flex min-w-0 shrink-0 items-center", className)}
      style={{ aspectRatio: `${asset.width} / ${asset.height}` }}
    >
      <img
        alt={asset.alt}
        className={cn(imageClass, srcDark && "dark:hidden")}
        decoding="async"
        fetchPriority={priority ? "high" : undefined}
        height={asset.height}
        loading={priority ? "eager" : "lazy"}
        src={asset.src}
        width={asset.width}
      />
      {srcDark ? (
        <img
          alt={asset.alt}
          className={cn("hidden dark:block", imageClass)}
          decoding="async"
          fetchPriority={priority ? "high" : undefined}
          height={asset.height}
          loading={priority ? "eager" : "lazy"}
          src={srcDark}
          width={asset.width}
        />
      ) : null}
    </span>
  );
}
